import { Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { RouterProvider, type RouterHistory } from "@tanstack/react-router";
import { I18nextProvider, useTranslation } from "react-i18next";
import { MotionConfig } from "motion/react";
import type { i18n as I18n } from "i18next";
import { getGetMeQueryKey, useGetMe, useGetWorkspacePreferences, useUpdateMe } from "@brevalt/api-client/customer";
import {
  AuthGate,
  AuthProvider,
  createQueryClient,
  ErrorBoundary,
  EventsProvider,
  I18nKitCopy,
  IdleTimeout,
  logoutEverywhere,
  matchLocale,
  readStoredLocale,
  useSessionUser,
  StepUpProvider,
  useAuth,
  useErrorToast,
  useTitledErrorToast,
  type AuthSession,
} from "@brevalt/app-core";
import { ToastProvider } from "@brevalt/ui";
import { LOCALES, localeRuntime } from "@brevalt/app-core/locale-runtime";
import { createAppRouter } from "./router";
import { RestCopy } from "./copy-rest";
import { config, idleMinutesFor, idleMinutesOf } from "./config";

/** Opens the event stream once the member is known (permission-filtered, product.read). */
function LiveEvents({ children }: { children: ReactNode }) {
  const me = useGetMe();
  const permissions = me.data?.currentTenant?.permissions ?? [];
  return (
    <EventsProvider permissions={permissions} enabled={me.isSuccess} workspaceKey={me.data?.currentTenant?.tenantId}>
      {children}
    </EventsProvider>
  );
}

/**
 * Profile versus device (L1, lead decision ii). A profile with a locale wins: the app switches to it (no reload) and
 * remembers it here. A profile without one takes this device's explicit choice (stored here, or picked on
 * Keycloak's pages: the token's `locale`) and saves it to the profile once, so other devices follow. The browser's
 * languages alone are not a choice and are never saved.
 */
function ProfileLocale() {
  const me = useGetMe();
  const user = useSessionUser();
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const update = useUpdateMe({ mutation: { meta: { toastOnError: false } } });
  const saved = useRef(false);
  const loaded = me.isSuccess;
  const profile = matchLocale(me.data?.user.locale, LOCALES);
  // Saved only into an empty profile: a locale this build doesn't have ("tlh") is still the user's (L1b review M1).
  const profileEmpty = loaded && !me.data.user.locale;
  const device = matchLocale(readStoredLocale() ?? user?.locale, LOCALES);
  const wanted = loaded ? (profile ?? device) : null;
  const { mutate } = update;
  /** The locale last taken from the profile (or the device): a choice made here since then is not undone (L1d). */
  const applied = useRef<string | null>(null);
  useEffect(() => {
    if (!wanted) return;
    if (wanted !== applied.current && wanted !== i18n.language) {
      localeRuntime.switchTo(i18n, wanted).catch((e: unknown) => {
        // Not applied: the next run of this effect tries again (L1d review L1).
        if (applied.current === wanted) applied.current = null;
        console.error(`i18n: ${wanted} did not load`, e);
      });
    }
    applied.current = wanted;
    if (profileEmpty && !saved.current) {
      saved.current = true;
      mutate(
        { data: { locale: wanted } },
        { onSuccess: () => void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() }) },
      );
    }
  }, [wanted, profileEmpty, i18n, mutate, queryClient]);
  return null;
}

/**
 * The idle sign-out after the workspace's `sessionTimeoutMinutes` (15–360, B15 #76 b, #163 a; Settings →
 * Workspace). Until the preferences are read: the last value read in this workspace, else the build's default; after
 * a failed read (retried, then read again every minute in the background) the last value or the strictest 15
 * minutes, never the looser default (`idleMinutesFor`).
 */
function WorkspaceIdleTimeout() {
  const me = useGetMe();
  const tenant = me.data?.currentTenant?.tenantId ?? null;
  const prefs = useGetWorkspacePreferences({
    query: {
      enabled: !!tenant,
      retry: 3,
      refetchInterval: (q) => (q.state.status === "error" ? 60_000 : false),
      meta: { toastOnError: false },
    },
  });
  // The last value read, per workspace (a switch never carries another workspace's value over).
  const [last, setLast] = useState<{ tenant: string | null; minutes: number } | null>(null);
  const answered = prefs.isSuccess;
  const read = answered ? idleMinutesOf(prefs.data.sessionTimeoutMinutes) : null;
  if (read !== null && (last?.tenant !== tenant || last.minutes !== read)) setLast({ tenant, minutes: read });
  const lastKnown = last?.tenant === tenant ? last.minutes : null;
  const minutes = idleMinutesFor(
    { answered, value: prefs.data?.sessionTimeoutMinutes, failed: prefs.isError },
    lastKnown,
  );
  return <IdleTimeout key={minutes} minutes={minutes} />;
}

/** Step-up: the resumed action's result refreshes the data and shows an error if it failed. */
function StepUp({ children }: { children: ReactNode }) {
  const session = useAuth();
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const runNamed = useCallback(
    // "logout-everywhere" is the only named action so far.
    async (_named: "logout-everywhere") => {
      if ((await logoutEverywhere(session)) === "step-up-required") throw new Error("step-up did not take effect");
    },
    [session],
  );
  const onResult = useCallback(
    (_d: { action: string }, _result: unknown, error: unknown) => {
      if (error) toastError(error);
      void queryClient.invalidateQueries();
    },
    [queryClient, toastError],
  );
  return (
    <StepUpProvider runNamed={runNamed} onResult={onResult}>
      {children}
    </StepUpProvider>
  );
}

function SignedIn({ history }: { history?: RouterHistory }) {
  // A mutation's `meta.errorTitle` gives the toast its own sentence (case refusals by code, REQUESTS #152).
  const toastError = useTitledErrorToast();
  const queryClient = useMemo(() => createQueryClient({ onError: toastError }), [toastError]);
  const router = useMemo(() => createAppRouter(history), [history]);
  // The pages wait for the rest of the copy (src/copy-rest.tsx); `/me` and the event stream don't (F14 perf).
  return (
    <QueryClientProvider client={queryClient}>
      <StepUp>
        <WorkspaceIdleTimeout />
        <ProfileLocale />
        <LiveEvents>
          <Suspense fallback={null}>
            <RestCopy>
              <RouterProvider router={router} />
            </RestCopy>
          </Suspense>
        </LiveEvents>
      </StepUp>
    </QueryClientProvider>
  );
}

export function App({
  i18n,
  session,
  history,
  location,
}: {
  i18n: I18n;
  session: AuthSession;
  history?: RouterHistory;
  /** Tests: the location AuthGate reads (defaults to the browser's). */
  location?: Pick<Location, "pathname" | "search" | "hash" | "href">;
}) {
  return (
    <I18nextProvider i18n={i18n}>
      {/* The kit's words and dates in the active language (#141 a); its keys are in the first catalogue. */}
      <I18nKitCopy>
        <MotionConfig reducedMotion="user">
          <ToastProvider>
            <ErrorBoundary>
              <AuthProvider session={session}>
                <AuthGate idleMinutes={config.idleMinutes} {...(location ? { location } : {})}>
                  <SignedIn history={history} />
                </AuthGate>
              </AuthProvider>
            </ErrorBoundary>
          </ToastProvider>
        </MotionConfig>
      </I18nKitCopy>
    </I18nextProvider>
  );
}
